import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState, useCallback } from "react";
import { getDecisionRunDetail } from "@/lib/decision-run-detail.functions";
import { PortalShell } from "@/components/portal/PortalShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Download, Eye } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  RunOverviewCard,
  VariantComparisonTable,
  DecisionCards,
  EconomicsSection,
  MieterSection,
  QualitySection,
  OfferComparisonSection,
  BuildingSection,
  ComplianceSection,
  CustomerSummarySection,
} from "@/components/portal/RunDetailSections";

const NAV = [
  { to: "/app/crm", label: "Übersicht" },
  { to: "/app/crm/customers", label: "Kunden" },
  { to: "/app/crm/leads", label: "Leads" },
  { to: "/app/crm/projects", label: "Projekte" },
];

export const Route = createFileRoute("/_authenticated/app/crm/projects/$id/runs/$runId")({
  ssr: false,
  head: () => ({ meta: [{ title: "Run-Details" }, { name: "robots", content: "noindex,nofollow" }] }),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["decision-run-detail", params.runId],
      queryFn: () => getDecisionRunDetail({ data: { run_id: params.runId } }),
    }),
  component: RunDetailPage,
});

function fmtDate(ts: string | null) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("de-DE");
}

function fmtEUR(n: number | null) {
  if (n == null) return "—";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

function fmtPct(n: number | null) {
  if (n == null) return "—";
  return `${n.toFixed(1)} %`;
}

function fmtNum(n: number | null, digits = 1) {
  if (n == null) return "—";
  return n.toFixed(digits);
}

type CSVOptions = {
  dateFormat: "iso" | "locale";
  showDetail: boolean;
};

function fmtDateCSV(ts: number, opts: CSVOptions) {
  const d = new Date(ts);
  if (opts.dateFormat === "iso") return d.toISOString();
  return d.toLocaleString("de-DE");
}

function buildReconnectLogCSV(log: ReconnectLogEntry[], opts: CSVOptions): string {
  const headers = ["Zeit", "Versuch", "Ergebnis", ...(opts.showDetail ? ["Detail"] : [])];
  const rows = [...log].reverse().map((e) => {
    const base = [fmtDateCSV(e.at, opts), String(e.attempt), e.result];
    if (opts.showDetail) base.push(e.detail ?? "");
    return base;
  });
  return [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
    .join("\r\n");
}

function downloadReconnectLogCSV(log: ReconnectLogEntry[], filename: string, opts: CSVOptions) {
  const csv = buildReconnectLogCSV(log, opts);
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const LIVE_STATES = new Set(["queued", "pending", "running", "in_progress", "started"]);

type RtState = "connecting" | "live" | "offline";

type ReconnectLogEntry = {
  at: number;
  attempt: number;
  result: "scheduled" | "success" | "error" | "manual";
  detail?: string;
};

function RunDetailPage() {
  const { id: projectId, runId } = Route.useParams();
  const getFn = useServerFn(getDecisionRunDetail);
  const queryClient = useQueryClient();
  const [rt, setRt] = useState<RtState>("connecting");
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [nextRetryAt, setNextRetryAt] = useState<number | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const reconnectRef = useRef<{ timer: ReturnType<typeof setTimeout> | null; attempt: number }>({ timer: null, attempt: 0 });
  const [reconnectLog, setReconnectLog] = useState<ReconnectLogEntry[]>([]);
  const [csvDateFormat, setCsvDateFormat] = useState<"iso" | "locale">("locale");
  const [csvShowDetail, setCsvShowDetail] = useState(true);
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);

  const logReconnect = useCallback((entry: Omit<ReconnectLogEntry, "at">) => {
    setReconnectLog((prev) => [{ at: Date.now(), ...entry }, ...prev].slice(0, 10));
  }, []);

  const { data, isFetching, dataUpdatedAt, refetch } = useSuspenseQuery({
    queryKey: ["decision-run-detail", runId],
    queryFn: () => getFn({ data: { run_id: runId } }),
    refetchOnWindowFocus: true,
  });

  const { run, variants } = data;
  const isLive = LIVE_STATES.has(run.status);

  // Stabiler Channel-Setup mit exponentiellem Backoff bei Verbindungsabbrüchen.
  const connect = useCallback(() => {
    setRt("connecting");
    const channel = supabase
      .channel(`decision-run-${runId}-${Date.now()}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "decision_runs", filter: `id=eq.${runId}` },
        () => {
          setLastEventAt(Date.now());
          queryClient.invalidateQueries({ queryKey: ["decision-run-detail", runId] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "decision_variants", filter: `decision_run_id=eq.${runId}` },
        () => {
          setLastEventAt(Date.now());
          queryClient.invalidateQueries({ queryKey: ["decision-run-detail", runId] });
        },
      )
      .subscribe((status, err) => {
        if (status === "SUBSCRIBED") {
          setRt("live");
          setAttempt(0);
          setNextRetryAt(null);
          setLastError(null);
          reconnectRef.current.attempt = 0;
          logReconnect({ attempt: 0, result: "success" });
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
          setRt("offline");
          const detail = err?.message ?? `Verbindung verloren (${status})`;
          setLastError(detail);
          logReconnect({ attempt: reconnectRef.current.attempt, result: "error", detail });
          scheduleReconnect();
        }
      });
    return channel;
  }, [runId, queryClient, logReconnect]);

  const scheduleReconnect = useCallback(() => {
    const next = Math.min(reconnectRef.current.attempt + 1, 8);
    reconnectRef.current.attempt = next;
    // Exponentiell: 1s, 2s, 4s, 8s, 16s, 32s, max 60s
    const delayMs = Math.min(1000 * Math.pow(2, next - 1), 60_000);
    setAttempt(next);
    setNextRetryAt(Date.now() + delayMs);
    logReconnect({ attempt: next, result: "scheduled", detail: `Retry in ${delayMs}ms` });
    if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
    reconnectRef.current.timer = setTimeout(() => {
      // Bei Reconnect auch sofort frische Daten holen, da Events zwischenzeitlich verloren sein könnten.
      refetch();
      const ch = connect();
      activeChannelRef.current = ch;
    }, delayMs);
  }, [connect, refetch, logReconnect]);

  const activeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    activeChannelRef.current = connect();
    return () => {
      if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
      reconnectRef.current.timer = null;
      reconnectRef.current.attempt = 0;
      if (activeChannelRef.current) supabase.removeChannel(activeChannelRef.current);
      activeChannelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Countdown-Ticker für "Nächster Versuch in …"
  useEffect(() => {
    if (rt !== "offline" || !nextRetryAt) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [rt, nextRetryAt]);

  const manualReconnect = useCallback(() => {
    if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
    reconnectRef.current.attempt = 0;
    setAttempt(0);
    setNextRetryAt(null);
    if (activeChannelRef.current) supabase.removeChannel(activeChannelRef.current);
    logReconnect({ attempt: 0, result: "manual" });
    refetch();
    activeChannelRef.current = connect();
  }, [connect, refetch, logReconnect]);

  const retrySecs = nextRetryAt ? Math.max(0, Math.ceil((nextRetryAt - now) / 1000)) : null;



  const durationSec =
    run.started_at && run.finished_at
      ? Math.round((new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000)
      : null;

  return (
    <PortalShell nav={NAV}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Run-Details</h1>
          <p className="text-sm text-muted-foreground font-mono">{run.project_no} · {run.project_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {rt === "live" ? (
            <Badge className="bg-emerald-600 text-white text-xs flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
              Live-Stream aktiv{isLive ? " · Run läuft" : ""}
              {lastEventAt && <span className="opacity-80"> · {new Date(lastEventAt).toLocaleTimeString("de-DE")}</span>}
            </Badge>
          ) : rt === "connecting" ? (
            <Badge variant="outline" className="text-xs flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
              Verbinde Live-Stream…
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs flex items-center gap-1.5 border-red-500/40 text-red-700 dark:text-red-300">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
              Live-Stream offline
              {retrySecs != null && <span className="opacity-80"> · neuer Versuch in {retrySecs}s</span>}
            </Badge>
          )}
          <Link to="/app/crm/projects/$id" params={{ id: projectId }}>
            <Button variant="outline" size="sm">Zurück zum Projekt</Button>
          </Link>
        </div>
      </div>

      {rt === "offline" && (
        <div
          role="alert"
          aria-live="assertive"
          className="mb-6 flex flex-col gap-3 rounded-md border border-red-500/40 bg-red-50/60 dark:bg-red-950/20 px-4 py-3 text-sm text-red-900 dark:text-red-100 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="space-y-1">
            <div className="font-medium">Live-Stream unterbrochen</div>
            <div className="text-red-800/90 dark:text-red-200/90">
              Die Echtzeit-Verbindung zur Datenbank ist abgerissen. Aktualisierungen werden derzeit nicht automatisch angezeigt.
              {retrySecs != null && (
                <> Automatischer Reconnect-Versuch in <span className="font-mono">{retrySecs}s</span> (Versuch #{attempt}).</>
              )}
            </div>
            {lastError && (
              <details className="text-xs text-red-800/70 dark:text-red-200/70">
                <summary className="cursor-pointer">Technische Details</summary>
                <pre className="mt-1 whitespace-pre-wrap break-words">{lastError}</pre>
              </details>
            )}
            <div className="text-xs text-red-800/70 dark:text-red-200/70">
              Letzter Datenstand: {new Date(dataUpdatedAt).toLocaleTimeString("de-DE")}{isFetching ? " · lade …" : ""}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={manualReconnect} className="border-red-500/40 shrink-0">
            Jetzt neu verbinden
          </Button>
        </div>
      )}

      {reconnectLog.length > 0 && (<div>
        <Card className="mb-6">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">Reconnect-Historie</CardTitle>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="csv-date" className="text-xs text-muted-foreground">Datum:</Label>
                <Select value={csvDateFormat} onValueChange={(v) => setCsvDateFormat(v as "iso" | "locale")}>
                  <SelectTrigger id="csv-date" className="h-8 w-32 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="locale">Deutsch</SelectItem>
                    <SelectItem value="iso">ISO 8601</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="csv-detail" className="text-xs text-muted-foreground">Detail:</Label>
                <Switch id="csv-detail" checked={csvShowDetail} onCheckedChange={setCsvShowDetail} />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCsvPreviewOpen(true)}
              >
                <Eye className="h-4 w-4 mr-1.5" />
                Vorschau
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadReconnectLogCSV(reconnectLog, `reconnect-log-${runId}.csv`, { dateFormat: csvDateFormat, showDetail: csvShowDetail })}
              >
                <Download className="h-4 w-4 mr-1.5" />
                CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Zeit</TableHead>
                    <TableHead className="w-20">Versuch</TableHead>
                    <TableHead className="w-28">Ergebnis</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reconnectLog.map((entry, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-xs font-mono">{new Date(entry.at).toLocaleTimeString("de-DE")}</TableCell>
                      <TableCell className="text-xs">{entry.attempt}</TableCell>
                      <TableCell>
                        {entry.result === "success" ? (
                          <Badge className="bg-emerald-600 text-white text-xs">Erfolg</Badge>
                        ) : entry.result === "error" ? (
                          <Badge variant="outline" className="text-xs border-red-500/40 text-red-700 dark:text-red-300">Fehler</Badge>
                        ) : entry.result === "scheduled" ? (
                          <Badge variant="outline" className="text-xs">Geplant</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Manuell</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{entry.detail ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        <Dialog open={csvPreviewOpen} onOpenChange={setCsvPreviewOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>CSV-Vorschau</DialogTitle>
              <DialogDescription>
                Vorschau der Reconnect-Historie im CSV-Format mit den aktuellen Einstellungen.
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-x-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Zeit</TableHead>
                    <TableHead>Versuch</TableHead>
                    <TableHead>Ergebnis</TableHead>
                    {csvShowDetail && <TableHead>Detail</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...reconnectLog].reverse().map((entry, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-xs font-mono whitespace-nowrap">{fmtDateCSV(entry.at, { dateFormat: csvDateFormat, showDetail: csvShowDetail })}</TableCell>
                      <TableCell className="text-xs">{entry.attempt}</TableCell>
                      <TableCell className="text-xs">{entry.result}</TableCell>
                      {csvShowDetail && (
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{entry.detail ?? "—"}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>
      </div>)}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Run-Metadaten</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div><span className="text-muted-foreground">Run-ID:</span> <span className="font-mono">{run.id}</span></div>
            <div><span className="text-muted-foreground">Engine-Version:</span> {run.engine_version}</div>
            <div><span className="text-muted-foreground">Ruleset-Version:</span> {run.ruleset_version ?? "—"}</div>
            <div><span className="text-muted-foreground">Prompt-Version:</span> {run.prompt_version ?? "—"}</div>
            <div><span className="text-muted-foreground">Phase:</span> <Badge variant="secondary">{run.phase_type}</Badge></div>
            <div><span className="text-muted-foreground">Status:</span> <Badge variant="secondary">{run.status}</Badge></div>
            <div><span className="text-muted-foreground">Trigger:</span> {run.trigger_source ?? "—"}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Zeitfenster</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div><span className="text-muted-foreground">Gestartet:</span> {fmtDate(run.started_at)}</div>
            <div><span className="text-muted-foreground">Beendet:</span> {fmtDate(run.finished_at)}</div>
            <div><span className="text-muted-foreground">Dauer:</span> {durationSec != null ? `${durationSec} s` : "—"}</div>
            <div><span className="text-muted-foreground">Erstellt:</span> {fmtDate(run.created_at)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Ergebnis-Summary</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div><span className="text-muted-foreground">Varianten:</span> {run.variant_count}</div>
            <div><span className="text-muted-foreground">Economically Best:</span> {run.economically_best_code ?? "—"}</div>
            <div><span className="text-muted-foreground">Energy Prime:</span> {run.energy_prime_code ?? "—"}</div>
            <div><span className="text-muted-foreground">Tie-Breaker:</span> {run.tie_breaker_applied === true ? "Ja" : run.tie_breaker_applied === false ? "Nein" : "—"}</div>
            {run.tie_breaker_reason && (
              <div><span className="text-muted-foreground">Tie-Breaker-Grund:</span> {run.tie_breaker_reason}</div>
            )}
            {run.last_error && (
              <div className="text-red-600 dark:text-red-400"><span className="text-muted-foreground">Fehler:</span> {run.last_error}</div>
            )}
          </CardContent>
        </Card>
      </div>

      <CustomerSummarySection run={run} variants={variants} />
      <RunOverviewCard run={run} variants={variants} />
      <DecisionCards variants={variants} />
      <VariantComparisonTable variants={variants} />
      <EconomicsSection variants={variants} />
      <MieterSection variants={variants} />
      <QualitySection variants={variants} />
      <OfferComparisonSection variants={variants} />
      <BuildingSection variants={variants} />
      <ComplianceSection variants={variants} />

      {run.output_summary && Object.keys(run.output_summary).length > 0 && (
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-base">Technische Details (Output-Summary JSON)</CardTitle></CardHeader>
          <CardContent>
            <details>
              <summary className="cursor-pointer text-xs text-muted-foreground mb-2">JSON anzeigen</summary>
              <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words">
                {JSON.stringify(run.output_summary, null, 2)}
              </pre>
            </details>
          </CardContent>
        </Card>
      )}
    </PortalShell>
  );
}
